import React, { useState, useEffect, useMemo, useRef } from 'react';
import { notify } from '../utils/ToastUtils';
import moment from 'moment';
import { Clock, CalendarCheck, ChevronLeft, ChevronRight, Download, Calendar as CalendarIcon, CalendarRange, Filter, X, Info } from "lucide-react";
import { Skeleton, Tooltip, Popover, Radio, RadioGroup, FormControlLabel, FormControl, Button, Box } from '@mui/material';
import * as XLSX from "xlsx";

export default function TimesheetSummaryTable({
    rawTimesheetData = [],
    projects = [],
    isApiLoading = false,
    showFiltersToggle = false,
    filterState = {},
    onFilterChange = () => {},
    onFilterSubmit = () => {},
    isFilterDisabled = false
}) {
    const [viewMode, setViewMode] = useState('weekly');
    const [currentDate, setCurrentDate] = useState(moment());
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterRef = useRef(null);

    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [exportMode, setExportMode] = useState("all");
    const [exportFilename, setExportFilename] = useState("");

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
        setExportFilename("");
    };

    useEffect(() => {
        if (rawTimesheetData && rawTimesheetData.length > 0) {
            const earliestDate = moment.min(rawTimesheetData.map(d => moment(d.WORK_DAY)));
            setCurrentDate(moment(earliestDate));
        }
    }, [rawTimesheetData]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setIsFilterOpen(false);
            }
        }
        if (isFilterOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isFilterOpen]);

    const getProjectName = (code) => {
        const project = projects.find(el => el?.PROJECT_CODE === code);
        return project?.PROJECT_NAME || code;
    };

    const displayData = useMemo(() => {
        if (!rawTimesheetData || rawTimesheetData.length === 0) return { columns: [], rows: [], totals: {}, periodLabel: '' };

        const projectCodes = [...new Set(rawTimesheetData.map(d => d.PROJECT_CODE))];

        if (viewMode === 'weekly') {
            const startOfWeek = moment(currentDate).startOf('isoWeek');
            const endOfWeek = moment(currentDate).endOf('isoWeek');
            
            const columns = [];
            for (let m = moment(startOfWeek); m.isSameOrBefore(endOfWeek); m.add(1, 'days')) {
                columns.push({
                    date: m.format('YYYY-MM-DD'),
                    dayName: m.format('ddd'),
                    dayNum: m.format('DD MMM')
                });
            }

            const rows = projectCodes.map(code => {
                const rowData = { projectCode: code, projectName: getProjectName(code), hours: {}, total: 0 };
                columns.forEach(col => {
                    const dayData = rawTimesheetData.filter(d => d.PROJECT_CODE === code && moment(d.WORK_DAY).format('YYYY-MM-DD') === col.date);
                    const hours = dayData.reduce((sum, d) => sum + (d.WORK_HOURS || 0), 0);
                    rowData.hours[col.date] = hours;
                    rowData.total += hours;
                });
                return rowData;
            }).filter(r => r.total > 0);

            const totals = { total: 0 };
            columns.forEach(col => {
                let colTotal = 0;
                rows.forEach(row => {
                    colTotal += row.hours[col.date];
                });
                totals[col.date] = colTotal;
                totals.total += colTotal;
            });

            const weekNum = Math.ceil(startOfWeek.date() / 7) || 1;
            const periodLabel = `Week ${weekNum} (${startOfWeek.format('DD MMM')} - ${endOfWeek.format('DD MMM YYYY')})`;

            return { columns, rows, totals, periodLabel };
        } else {
            const startOfMonth = moment(currentDate).startOf('month');
            const endOfMonth = moment(currentDate).endOf('month');

            const weeks = [];
            let currentWeekStart = moment(startOfMonth);
            let weekNum = 1;
            
            while (currentWeekStart.isSameOrBefore(endOfMonth)) {
                const currentWeekEnd = moment(currentWeekStart).add(6, 'days');
                const end = currentWeekEnd.isAfter(endOfMonth) ? endOfMonth : currentWeekEnd;
                
                weeks.push({
                    label: `Week ${weekNum}`,
                    subLabel: `(${currentWeekStart.format('DD MMM')} - ${end.format('DD MMM')})`,
                    start: currentWeekStart.format('YYYY-MM-DD'),
                    end: end.format('YYYY-MM-DD'),
                    key: `week${weekNum}`
                });
                currentWeekStart = moment(currentWeekEnd).add(1, 'days');
                weekNum++;
            }

            const columns = weeks;

            const rows = projectCodes.map(code => {
                const rowData = { projectCode: code, projectName: getProjectName(code), hours: {}, total: 0 };
                columns.forEach(col => {
                    const weekHours = rawTimesheetData.filter(d => 
                        d.PROJECT_CODE === code && 
                        moment(d.WORK_DAY).isSameOrAfter(col.start) && 
                        moment(d.WORK_DAY).isSameOrBefore(col.end)
                    ).reduce((sum, d) => sum + d.WORK_HOURS, 0);

                    rowData.hours[col.key] = weekHours;
                    rowData.total += weekHours;
                });
                return rowData;
            }).filter(r => r.total > 0);

            const totals = { total: 0 };
            columns.forEach(col => {
                let colTotal = 0;
                rows.forEach(row => {
                    colTotal += row.hours[col.key];
                });
                totals[col.key] = colTotal;
                totals.total += colTotal;
            });

            return { columns, rows, totals, periodLabel: currentDate.format('MMMM YYYY') };
        }
    }, [rawTimesheetData, viewMode, currentDate, projects]);

    const canGoPrev = useMemo(() => {
        if (!filterState.startDate && rawTimesheetData.length === 0) return true;
        
        let startBoundary = moment(filterState.startDate);
        if (!filterState.startDate) {
            // fallback if filterState not provided, just allow endless scroll
            return true;
        }

        const prevPeriodEnd = moment(currentDate)
            .subtract(1, viewMode === 'weekly' ? 'weeks' : 'months')
            .endOf(viewMode === 'weekly' ? 'isoWeek' : 'month');
        return prevPeriodEnd.isSameOrAfter(startBoundary, 'day');
    }, [currentDate, viewMode, filterState.startDate, rawTimesheetData]);

    const canGoNext = useMemo(() => {
        if (!filterState.endDate && rawTimesheetData.length === 0) return true;
        
        let endBoundary = moment(filterState.endDate);
        if (!filterState.endDate) {
            return true;
        }

        const nextPeriodStart = moment(currentDate)
            .add(1, viewMode === 'weekly' ? 'weeks' : 'months')
            .startOf(viewMode === 'weekly' ? 'isoWeek' : 'month');
        return nextPeriodStart.isSameOrBefore(endBoundary, 'day');
    }, [currentDate, viewMode, filterState.endDate, rawTimesheetData]);

    const navigatePeriod = (direction) => {
        if (direction === 'prev' && !canGoPrev) return;
        if (direction === 'next' && !canGoNext) return;
        
        if (viewMode === 'weekly') {
            setCurrentDate(moment(currentDate).add(direction === 'next' ? 1 : -1, 'weeks'));
        } else {
            setCurrentDate(moment(currentDate).add(direction === 'next' ? 1 : -1, 'months'));
        }
    };

    const handleConfirmExport = () => {
        if (exportMode === "view") {
            if (!displayData.rows || displayData.rows.length === 0) {
                notify.info("No data to export for this period");
                handleExportClose();
                return;
            }

            let headers = ["Project"];
            if (viewMode === 'weekly') {
                displayData.columns.forEach(col => headers.push(`${col.dayName} ${col.dayNum}`));
            } else {
                displayData.columns.forEach(col => headers.push(`${col.label} ${col.subLabel}`));
            }
            headers.push("Total");

            const aoa = [headers];

            displayData.rows.forEach(row => {
                let rowData = [row.projectName];
                displayData.columns.forEach(col => {
                    const key = viewMode === 'weekly' ? col.date : col.key;
                    rowData.push(row.hours[key] || 0);
                });
                rowData.push(row.total);
                aoa.push(rowData);
            });

            let totalsRow = ["Total"];
            displayData.columns.forEach(col => {
                const key = viewMode === 'weekly' ? col.date : col.key;
                totalsRow.push(displayData.totals[key] || 0);
            });
            totalsRow.push(displayData.totals.total || 0);
            aoa.push(totalsRow);

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            XLSX.utils.book_append_sheet(wb, ws, "Current View");
            
            let filename = exportFilename.trim().replace(/\s+/g, '_');
            if (!filename) filename = `Timesheet_${viewMode}_${currentDate.format('YYYY-MM-DD')}.xlsx`;
            else if (!filename.toLowerCase().endsWith('.xlsx')) filename += '.xlsx';
            
            XLSX.writeFile(wb, filename);

        } else {
            if (!rawTimesheetData || rawTimesheetData.length === 0) {
                notify.info("No data to export");
                handleExportClose();
                return;
            }
            
            // 1. Build Summary Sheet (Group by Month)
            const monthsSet = new Set();
            rawTimesheetData.forEach(row => {
                if (row.WORK_DAY) {
                    monthsSet.add(moment(row.WORK_DAY).startOf('month').format('YYYY-MM'));
                }
            });
            
            const sortedMonths = Array.from(monthsSet).sort();
            const monthLabels = sortedMonths.map(m => moment(m, 'YYYY-MM').format('MMM-YY'));
            
            const projectTotals = {};
            const monthTotals = {};
            let grandTotal = 0;

            sortedMonths.forEach(m => monthTotals[m] = 0);

            rawTimesheetData.forEach(row => {
                const pCode = row.PROJECT_CODE;
                const mKey = moment(row.WORK_DAY).startOf('month').format('YYYY-MM');
                const hours = row.WORK_HOURS || 0;

                if (!projectTotals[pCode]) {
                    projectTotals[pCode] = { name: getProjectName(pCode), total: 0, months: {} };
                    sortedMonths.forEach(m => projectTotals[pCode].months[m] = 0);
                }
                if (projectTotals[pCode].months[mKey] !== undefined) {
                    projectTotals[pCode].months[mKey] += hours;
                    monthTotals[mKey] += hours;
                }
                projectTotals[pCode].total += hours;
                grandTotal += hours;
            });

            const summaryAOA = [];
            summaryAOA.push(["Project", ...monthLabels, "Total"]);

            Object.values(projectTotals).sort((a, b) => a.name.localeCompare(b.name)).forEach(p => {
                if (p.total > 0) {
                    const row = [p.name];
                    sortedMonths.forEach(m => row.push(p.months[m]));
                    row.push(p.total);
                    summaryAOA.push(row);
                }
            });

            const footer = ["Total"];
            sortedMonths.forEach(m => footer.push(monthTotals[m]));
            footer.push(grandTotal);
            summaryAOA.push(footer);

            // 2. Build Detailed Sheet
            const detailData = rawTimesheetData.map(row => ({
                "Date": row.WORK_DAY ? moment(row.WORK_DAY).format('DD-MMM-YYYY') : "",
                "Employee": row.EMP_NAME || row.CODE || "",
                "Project": getProjectName(row.PROJECT_CODE),
                "Hours": row.WORK_HOURS || 0,
                // "Task": row.TASK || "",
                // "Task Type": row.Task_Type || "",
                // "Description": row.Description || ""
            }));

            // 3. Create Workbook
            const wb = XLSX.utils.book_new();
            const wsSummary = XLSX.utils.aoa_to_sheet(summaryAOA);
            XLSX.utils.book_append_sheet(wb, wsSummary, "Timesheet Summary");

            const wsDetail = XLSX.utils.json_to_sheet(detailData);
            XLSX.utils.book_append_sheet(wb, wsDetail, "Detailed Data");

            let filename = exportFilename.trim().replace(/\s+/g, '_');
            if (!filename) filename = `Timesheet_All_Data_${moment().format('YYYY-MM-DD')}.xlsx`;
            else if (!filename.toLowerCase().endsWith('.xlsx')) filename += '.xlsx';

            XLSX.writeFile(wb, filename);
        }
        handleExportClose();
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
            <div className="mb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100 shadow-sm">
                    <CalendarCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                    <h2 className="text-[16px] font-bold text-slate-800 tracking-tight flex items-center gap-2">
                        Timesheet Summary
                    </h2>
                    <p className="text-xs font-medium text-slate-500 mt-0.5 flex items-center gap-1.5">
                        Overview of logged hours by date and project.
                    </p>
                </div>
            </div>

            {/* Custom Table with Toolbar */}
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm relative">
                {/* Table Controls (View Toggles, Period Navigator, Export) */}
                <div className="flex flex-wrap items-center justify-center lg:justify-between gap-4 p-4 border-b border-slate-200 bg-slate-50/50 rounded-t-xl">
                    <div className="flex flex-1 justify-center lg:justify-start">
                        <div className="inline-flex items-center bg-white border border-slate-200 rounded-lg shadow-sm">
                            <button
                                onClick={() => setViewMode("weekly")}
                                className={`flex items-center justify-center gap-2 min-w-[150px] px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                                viewMode === "weekly"
                                    ? "bg-blue-50 text-blue-600"
                                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                                }`}
                            >
                                <CalendarIcon className="w-4 h-4" />
                                Weekly View
                            </button>
                            <button
                                onClick={() => setViewMode("monthly")}
                                className={`flex items-center justify-center gap-2 min-w-[150px] px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                                viewMode === "monthly"
                                    ? "bg-blue-50 text-blue-600"
                                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                                }`}
                            >
                                <CalendarRange className="w-4 h-4" />
                                Monthly View
                            </button>
                        </div>
                    </div>

                    <div className="flex shrink-0 justify-center">
                        <div className="inline-flex items-center bg-white border border-slate-200 rounded-lg px-1 py-1 shadow-sm">
                            <button
                                onClick={() => navigatePeriod("prev")}
                                disabled={!canGoPrev}
                                className={`p-1.5 rounded-md transition-colors ${
                                    canGoPrev
                                        ? "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                        : "text-slate-400 opacity-50 cursor-not-allowed"
                                }`}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            <span className="px-3 text-sm font-semibold text-slate-700 whitespace-nowrap text-center">
                                {displayData.periodLabel}
                            </span>

                            <button
                                onClick={() => navigatePeriod("next")}
                                disabled={!canGoNext}
                                className={`p-1.5 rounded-md transition-colors ${
                                    canGoNext
                                        ? "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                        : "text-slate-400 opacity-50 cursor-not-allowed"
                                }`}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-1 justify-center lg:justify-end gap-2 relative" ref={filterRef}>
                        {showFiltersToggle && (
                            <button 
                                onClick={() => setIsFilterOpen(!isFilterOpen)}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
                            >
                                <Filter className="w-4 h-4" /> Filters
                            </button>
                        )}
                        <button 
                            onClick={handleExportClick}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
                        >
                            <Download className="w-4 h-4" /> Export
                        </button>

                        <Popover
                            open={Boolean(exportAnchorEl)}
                            anchorEl={exportAnchorEl}
                            onClose={handleExportClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            PaperProps={{
                                elevation: 3,
                                sx: { borderRadius: 3, mt: 1, minWidth: 320, border: '1px solid #e2e8f0', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)' }
                            }}
                        >
                            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h4 className="text-[15px] font-bold text-slate-800 m-0 mb-1 flex items-center gap-2">
                                            <Download className="w-4 h-4 text-blue-600" />
                                            Export Timesheets
                                        </h4>
                                        <p className="text-[11px] text-slate-500 m-0">Choose which data you would like to export to Excel.</p>
                                    </div>
                                    <button 
                                        onClick={handleExportClose} 
                                        className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-md hover:bg-slate-100 -mt-1 -mr-1"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                
                                <div className="space-y-2.5">
                                    <div 
                                        onClick={() => setExportMode("all")}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${exportMode === 'all' ? 'border-blue-500 bg-blue-50' : 'border-slate-100 hover:border-slate-200 bg-white shadow-sm'}`}
                                    >
                                        <div className={`mt-0.5 rounded-full p-1 border flex items-center justify-center transition-colors ${exportMode === 'all' ? 'border-blue-500 bg-blue-500 text-white' : 'border-slate-300 text-transparent'}`}>
                                            <div className="w-2 h-2 rounded-full bg-current" />
                                        </div>
                                        <div className="flex-1">
                                            <h5 className={`text-[14px] font-bold mb-1 transition-colors ${exportMode === 'all' ? 'text-blue-800' : 'text-slate-700'}`}>Full Date Range</h5>
                                        </div>
                                    </div>

                                    <div 
                                        onClick={() => setExportMode("view")}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${exportMode === 'view' ? 'border-blue-500 bg-blue-50' : 'border-slate-100 hover:border-slate-200 bg-white shadow-sm'}`}
                                    >
                                        <div className={`mt-0.5 rounded-full p-1 border flex items-center justify-center transition-colors ${exportMode === 'view' ? 'border-blue-500 bg-blue-500 text-white' : 'border-slate-300 text-transparent'}`}>
                                            <div className="w-2 h-2 rounded-full bg-current" />
                                        </div>
                                        <div className="flex-1">
                                            <h5 className={`text-[14px] font-bold mb-1 transition-colors ${exportMode === 'view' ? 'text-blue-800' : 'text-slate-700'}`}>Current View Only</h5>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-1.5 mt-1">
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Custom Filename (Optional)</label>
                                    <input 
                                        type="text" 
                                        value={exportFilename}
                                        onChange={(e) => setExportFilename(e.target.value)}
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                                        placeholder={`Timesheet_${exportMode === 'all' ? 'All_Data' : viewMode}.xlsx`}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleConfirmExport();
                                        }}
                                    />
                                </div>

                                <Button 
                                    variant="contained" 
                                    size="medium" 
                                    onClick={handleConfirmExport}
                                    disableElevation
                                    className="w-full shadow-sm hover:shadow"
                                    sx={{ 
                                        textTransform: 'none', 
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        py: 1,
                                        borderRadius: '8px',
                                        bgcolor: '#2563eb',
                                        '&:hover': { bgcolor: '#1d4ed8' }
                                    }}
                                    startIcon={<Download size={16} />}
                                >
                                    Download Excel (.xlsx)
                                </Button>
                            </Box>
                        </Popover>

                        {/* Popover */}
                        {isFilterOpen && (
                            <div className="absolute top-[120%] right-0 p-4 bg-white border border-slate-200 rounded-xl shadow-xl z-50 w-[280px]">
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="text-sm font-bold text-slate-800">Filter Timesheet</h3>
                                    <button onClick={() => setIsFilterOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="flex flex-col gap-3">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Start Date</label>
                                        <input 
                                            type="date" 
                                            name="startDate"
                                            value={filterState.startDate || ""}
                                            onChange={onFilterChange}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">End Date</label>
                                        <input 
                                            type="date" 
                                            name="endDate"
                                            value={filterState.endDate || ""}
                                            onChange={onFilterChange}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={() => { onFilterSubmit(); setIsFilterOpen(false); }} 
                                        disabled={isFilterDisabled}
                                        className="mt-2 w-full px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                                        style={{opacity: isFilterDisabled ? '50%' : '100%'}}
                                    >
                                        Get Timesheet
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto rounded-b-xl">
                    <table className="w-full text-sm text-left border-collapse min-w-max">
                    <thead className="bg-white border-b border-slate-200 text-slate-600">
                        <tr>
                            <th className="px-3 py-4 font-bold text-slate-800 tracking-wide border-r border-slate-100 min-w-[120px]">Projects</th>
                            {displayData.columns.map(col => (
                                <th key={viewMode === 'weekly' ? col.date : col.key} className="px-2 py-4 text-center tracking-wide border-r border-slate-100">
                                    <div className="flex flex-col items-center">
                                        <span className="font-bold text-slate-800 whitespace-nowrap">{viewMode === 'weekly' ? col.dayName : col.label}</span>
                                        <span className="text-[12px] font-medium text-slate-500 mt-0.5 whitespace-nowrap">{viewMode === 'weekly' ? col.dayNum : col.subLabel}</span>
                                    </div>
                                </th>
                            ))}
                            <th className="px-4 py-4 font-bold text-slate-800 text-center tracking-wide bg-blue-50/50 border-l border-blue-100 whitespace-nowrap">Total</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {isApiLoading ? (
                            Array.from(new Array(3)).map((_, idx) => (
                                <tr key={`skeleton-${idx}`} className="bg-white">
                                    <td className="px-2 py-3">
                                        <Skeleton variant="text" width="70%" height={24} sx={{ borderRadius: 1 }} />
                                        <Skeleton variant="text" width="40%" height={16} sx={{ borderRadius: 1, mt: 0.5 }} />
                                    </td>
                                    {displayData.columns.map(col => (
                                        <td key={`sk-${viewMode === 'weekly' ? col.date : col.key}`} className="px-2 py-3">
                                            <div className="flex justify-center">
                                                <Skeleton variant="rounded" width={36} height={24} sx={{ borderRadius: 1.5 }} />
                                            </div>
                                        </td>
                                    ))}
                                    <td className="px-4 py-3 bg-blue-50/50 border-l border-blue-100 whitespace-nowrap">
                                        <div className="flex justify-center">
                                            <Skeleton variant="rounded" width={52} height={28} sx={{ borderRadius: 1.5 }} />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : displayData.rows.length === 0 ? (
                            <tr>
                                <td colSpan={displayData.columns.length + 2} className="px-5 py-12 text-center">
                                    <div className="flex flex-col items-center justify-center text-slate-400">
                                        <Clock className="w-10 h-10 mb-3 text-slate-300" />
                                        <p className="font-medium text-slate-500">No data available for this period</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            displayData.rows.map((row, idx) => (
                                <tr key={idx} className="bg-white hover:bg-slate-50/50 transition-colors">
                                    <td className="px-3 py-4 font-semibold text-slate-700 min-w-[120px]">
                                        <div className="line-clamp-2" title={row.projectName}>
                                            {row.projectName}
                                        </div>
                                    </td>
                                    {displayData.columns.map(col => {
                                        const val = row.hours[viewMode === 'weekly' ? col.date : col.key];
                                        return (
                                            <td key={viewMode === 'weekly' ? col.date : col.key} className="px-2 py-4 text-center font-medium text-slate-600">
                                                {val > 0 ? val : <span className="text-slate-300">-</span>}
                                            </td>
                                        );
                                    })}
                                    <td className="px-4 py-4 text-center font-bold text-blue-600 bg-blue-50/50 border-l border-blue-100 whitespace-nowrap">
                                        {row.total} <span className="text-[13px] font-semibold text-blue-600 ml-0.5">hrs</span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    {displayData.rows.length > 0 && (
                        <tfoot className="bg-blue-50/50 border-t border-blue-100">
                            <tr>
                                <td className="px-3 py-4 font-bold text-slate-800 whitespace-nowrap">Total</td>
                                {displayData.columns.map(col => (
                                    <td key={viewMode === 'weekly' ? col.date : col.key} className="px-2 py-4 text-center font-bold text-blue-600">
                                        {displayData.totals[viewMode === 'weekly' ? col.date : col.key]} <span className="text-[13px] font-semibold text-blue-600 ml-0.5">hrs</span>
                                    </td>
                                ))}
                                <td className="px-4 py-4 text-center border-l border-blue-100 whitespace-nowrap">
                                    <div className="inline-block bg-blue-600 text-white px-3 py-1.5 rounded-lg font-bold shadow-sm whitespace-nowrap">
                                        {displayData.totals.total} hrs
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    </div>
    );
}